import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { TalkToEngineerModal } from "@/components/TalkToEngineerModal";
import { ShareModal } from "@/components/ShareModal";
import { api, API } from "@/lib/api";
import { downloadPdf } from "@/lib/pdfDownload";
import { useAuth } from "@/context/AuthContext";
import { useT, getLocale, tr } from "@/lib/i18n";
import { applicationLabel } from "@/lib/application";
import { roadmapRows, roadmapLeadNote } from "@/lib/roadmap";
import { firmBasisPair, generatorDeratePct } from "@/lib/firmBasis";
import { equipmentRow, capexTech, capexRowLabel, sectorName, frNum } from "@/lib/resultsFr";
import { localisePermitting } from "@/lib/permittingI18n";
import { toast } from "sonner";
import { Download, PhoneCall, ArrowLeft, ShieldCheck, Share2, FileSpreadsheet, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RegulatorAuthority } from "@/components/RegulatorAuthority";
import { regimeLabel } from "@/lib/permittingI18n";
import { memoCountry, memoGrid, memoSector, memoParts, memoGridFr, memoPartsFr } from "@/lib/memoSummary";
import { CondFuelBanner } from "@/components/CondFuelBanner";
import { MemoMeta } from "@/components/MemoMeta";
import { comparisonMoney, comparisonYears } from "@/lib/comparison";
import { num, money, moneyM, pct, unit, perUnit, perYr, NBSP } from "@/lib/numfmt";

const usd = (n) => `${money(n || 0, 0)}`;
const fmt = (n) => num(n || 0, 1);
// Number + unit: EN keeps its plain space, FR takes U+00A0.
const kw = (v, u = "kW") => `${fmt(v)}${getLocale() === "fr" ? NBSP : " "}${u}`;
const PRIORITY_EN = { lowest_lifetime: "lowest lifetime cost", lowest_upfront: "lowest upfront cost",
  lowest_emission: "lowest emissions", max_reliability: "maximum reliability" };
const lcoeTxt = (v) => (getLocale() === "fr" ? perUnit(v, "kWh", 3, 0) : `$${v}/kWh`);

export const Report = () => {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const [project, setProject] = useState(null);
  const [talkOpen, setTalkOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // The language on screen (the switch, or French set by the country suggestion) is the default.
  const [pdfLang, setPdfLang] = useState(getLocale);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => {
      toast.error(tr("Report not found", "Rapport introuvable"));
      navigate("/projects");
    });
  }, [id, user, loading, navigate]);

  if (!project) return null;

  if (!project.paid) {
    return (
      <div className="App">
        <Header />
        <div className="max-w-[900px] mx-auto px-6 py-16 text-center">
          <h1 className="font-display text-3xl">{tr("Report locked", "Rapport verrouillé")}</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--muted)" }}>{tr("Purchase the detailed report or use a credit to unlock.", "Achetez le rapport détaillé ou utilisez un crédit pour le déverrouiller.")}</p>
          <button onClick={() => navigate("/projects")} className="btn-primary mt-6">{tr("Back to projects", "Retour aux projets")}</button>
        </div>
        <Footer />
      </div>
    );
  }

  const r = project.result;
  const input = project.input;
  const m = r.model || {};
  const cap = r.capex || {};
  const opex = r.opex || {};
  const fin = r.financials || {};
  const permits = r.permitting || {};
  const milestones = r.milestones || [];
  // §12 roadmap — the engine's schedule. Derived nowhere on this page.
  const sched = r.schedule || {};
  const leadNote = roadmapLeadNote(sched, t);

  // iter45ar — the ENGINE records what the recommendation should be CALLED
  // (`notes.recommended_label`; single author: engine.recommended_label_verdict).
  // This page READS that verdict — it never restates the rule, or the memo and
  // the PDF would eventually describe the same plant differently.
  const verdict = r.notes?.recommended_label || {};
  const isFullThermal = verdict.kind === "full_thermal_recommended";
  const isDieselOnly = verdict.kind === "diesel_only_recommended";
  const isFr = getLocale() === "fr";
  const lang = isFr ? "fr" : "en";
  const THERMAL_TECH_NAMES = isFr ? {
    ocgt: "turbines à gaz à cycle ouvert (OCGT)",
    ccgt: "turbines à gaz à cycle combiné (CCGT)",
    gas_engine: "moteurs alternatifs à gaz",
  } : {
    ocgt: "Open-cycle gas turbines (OCGT)",
    ccgt: "Combined-cycle gas turbines (CCGT)",
    gas_engine: "Reciprocating gas engines",
  };
  const thermalTechName =
    THERMAL_TECH_NAMES[String(m.thermal_tech || "").toLowerCase()] || (isFr ? "production thermique" : "thermal generation");
  // a37 — weak-grid and on-grid sites are compared with grid supply, not a diesel-only plant.
  const cfKind = fin.counterfactual?.kind;
  const gridCase = cfKind === "grid_plus_backup" || cfKind === "grid_supply";
  const cfName = cfKind === "grid_plus_backup"
    ? (isFr ? "réseau avec secours diesel" : "grid supply plus diesel back-up")
    : (isFr ? "réseau" : "grid supply");
  const prio = isFr ? t(`wiz.priority.${input.priority}`, input.priority) : input.priority?.replace(/_/g, " ");
  const prioInline = prio ? prio.charAt(0).toLowerCase() + prio.slice(1) : prio;
  const country = isFr ? t(`country.${input.country}`, input.country) : input.country;
  const shownPermits = localisePermitting(permits, lang);
  // Unit banding is recorded once on the model (`gas_units` / `gas_unit_size_kw`)
  // whichever machine the engine picked, so all three rows read it.
  const thermalSizing = (kwv) => {
    const total = Number(kwv || 0);
    if (!total) return "—";
    const units = Number(m.gas_units || 0);
    const unit = Number(m.gas_unit_size_kw || 0);
    return units > 0 && unit > 0
      ? `${units} × ${kw(unit)} = ${kw(total)}`
      : kw(total);
  };

  const downloadCsv = () => {
    try {
      const escape = (v) => {
        const s = v === null || v === undefined ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const num = (v) => (v === null || v === undefined || v === "" ? "" : Number(v));
      const pct = (v) => (v === null || v === undefined ? "" : `${Math.round(Number(v) * 100)}%`);
      const rows = [];
      const push = (...cells) => rows.push(cells.map(escape).join(","));

      push("Section", "Item", "Value", "Unit");

      // Project meta
      push("Project", "Name", project.name || "", "");
      push("Project", "Sector", input.sector || "", "");
      push("Project", "Application", applicationLabel(input), "");
      push("Project", "Country", input.country || "", "");
      push("Project", "Grid status", (input.grid_status || "").replace(/_/g, " "), "");
      push("Project", "Ownership", input.ownership || "", "");
      push("Project", "Priority", (input.priority || "").replace(/_/g, " "), "");
      push("Project", "Created at", project.created_at || "", "");

      // Power model. iter45ad — Fix mislabel: "Peak" prints input.peak_kw
      // (single source of truth from what the buyer entered). Firm capacity
      // target = peak × 1.20 is its own labelled row and MUST NOT collapse
      // with peak.
      push("Power model", "Peak load (entered)", num(input.peak_kw), "kW");
      push("Power model", "Firm capacity target (peak × 1.20)", num(m.firm_capacity_target_kw), "kW");
      // F3 RULED (Jun 2026) — the available figure is the AMBIENT-DERATED
      // plant. The nameplate and the derate are their own labelled rows so a
      // spreadsheet reader never has to guess which basis a number is on.
      push("Power model", "Firm capacity available (ambient-derated)", num(m.firm_capacity_available_kw), "kW");
      if (m.firm_capacity_installed_kw != null) {
        push("Power model", "Firm capacity installed (nameplate)", num(m.firm_capacity_installed_kw), "kW");
        push("Power model", "Ambient output derate applied", num(m.firm_capacity_derate_pct), "%");
        push("Power model", "Derate conditions",
             `${num(m.firm_capacity_derate_ambient?.temp_c)} °C / ${num(m.firm_capacity_derate_ambient?.altitude_m)} m`, "");
      }
      if (Number(m.battery_power_kw) > 0) {
        push("Power model", "Battery rated power", num(m.battery_power_kw), "kW");
        push("Power model", "Battery counted toward firm", num(m.firm_capacity_buildup?.battery_derated_kw), "kW");
      }
      push("Power model", "Firm capacity meets target", m.firm_capacity_ok ? "yes" : "no", "");
      push("Power model", "Solar PV", num(m.solar_kw), "kW");
      push("Power model", "Wind", num(m.wind_kw), "kW");
      push("Power model", "Battery energy", num(m.battery_kwh), "kWh");
      push("Power model", "Battery power", num(m.battery_power_kw), "kW");
      push("Power model", "Diesel gensets total", num(m.genset_kw), "kW");
      push("Power model", "Diesel gensets units", num(m.genset_units), "");
      push("Power model", "Diesel genset unit size", num(m.genset_unit_size_kw), "kW");
      push("Power model", "HFO", num(m.hfo_kw), "kW");
      push("Power model", "OCGT", num(m.ocgt_kw), "kW");
      push("Power model", "CCGT", num(m.ccgt_kw), "kW");
      push("Power model", "Reciprocating gas engine", num(m.gas_kw), "kW");
      push("Power model", "Thermal units", num(m.gas_units), "");
      push("Power model", "Thermal unit size", num(m.gas_unit_size_kw), "kW");
      push("Power model", "Annual demand", num(m.annual_demand_kwh || m.annual_energy_kwh), "kWh");
      push("Power model", "Annual served", num(m.annual_served_kwh || m.annual_energy_kwh), "kWh");
      push("Power model", "Annual generation (gross)", num(m.annual_generation_kwh), "kWh");
      push("Power model", "Renewable fraction", pct(m.renewable_fraction), "");
      push("Power model", "Generator derate", pct(generatorDeratePct(m), 1, 0), "");
      push("Power model", "PV specific yield", num(m.pv_yield_kwh_per_kwp_day), "kWh/kWp/day");

      // Equipment
      (r.equipment || []).forEach((e) => {
        push("Equipment", `${e.item}${e.size ? ` — ${e.size}` : ""}`, e.category || "", "");
      });

      // CAPEX
      (cap.breakdown || []).forEach((b) => {
        push("CAPEX breakdown", `${b.tech}${b.size ? ` (${b.size})` : ""}`, num(b.capex_usd), "USD");
      });
      push("CAPEX total", "Total (indicative)", num(cap.total), "USD");
      push("CAPEX total", "Range low", num(cap.range_low), "USD");
      push("CAPEX total", "Range high", num(cap.range_high), "USD");

      // OPEX
      push("OPEX (annual)", m.gas_kw > 0 ? "Fuel" : "Genset fuel", num(opex.fuel_cost), "USD");
      push("OPEX (annual)", "Solar O&M", num(opex.solar_om), "USD");
      if (m.battery_kwh > 0) push("OPEX (annual)", "Battery O&M", num(opex.battery_om), "USD");
      push("OPEX (annual)", m.gas_kw > 0 ? "Gas-engine O&M" : "Genset O&M", num(opex.genset_om), "USD");
      push("OPEX (annual)", "Grid import", num(opex.grid_cost), "USD");
      push("OPEX (annual)", "Total annual OPEX", num(opex.annual), "USD");

      // Financials
      push("Financials", "LCOE (indicative)", num(fin.lcoe_usd_per_kwh), "USD/kWh");
      push("Financials", "LCOE low", num(fin.lcoe_low), "USD/kWh");
      push("Financials", "LCOE high", num(fin.lcoe_high), "USD/kWh");
      push("Financials", "Discount rate", pct(fin.discount_rate), "");
      push("Financials", "Diesel-only baseline CAPEX", num(fin.baseline_diesel_only_capex), "USD");
      push("Financials", "Diesel-only baseline OPEX (annual)", num(fin.baseline_diesel_only_opex_annual), "USD");
      push("Financials", "Annual savings vs diesel-only (Y1)", comparisonMoney(fin.annual_savings_vs_diesel_yr1, num), "USD");
      push("Financials", "Simple payback", comparisonMoney(fin.simple_payback_years, num), "years");

      // Financing outline (illustrative, same split as report)
      push("Financing outline", "Equity (~30%)", Math.round((cap.total || 0) * 0.30), "USD");
      push("Financing outline", "Senior debt (~55%)", Math.round((cap.total || 0) * 0.55), "USD");
      push("Financing outline", "Grants / mezzanine (~15%)", Math.round((cap.total || 0) * 0.15), "USD");

      // Permitting
      push("Permitting", "Country", permits.country || input.country || "", "");
      push("Permitting", "Regime", regimeLabel(permits.regime || "", "en"), "");
      (permits.items || []).forEach((it) => {
        push("Permitting", `${it.item}${it.notes ? ` — ${it.notes}` : ""}`, it.status || "", it.authority || "");
      });

      // Milestones
      milestones.forEach((ph) => {
        const win = ph.start_month === null || ph.start_month === undefined
          ? ""
          : (ph.end_month === null || ph.end_month === undefined
              ? ` (from month ${ph.start_month})`
              : ` (months ${ph.start_month}–${ph.end_month})`);
        push("Milestone", `${ph.phase}${win} — ${(ph.activities || []).join("; ")}`, ph.duration_months ? `${ph.duration_months}` : "ongoing", ph.duration_months ? "months" : "");
      });
      (sched.milestones || []).forEach((ms) => {
        push("Milestone", t(`report.roadmap.${ms.key}`, ms.key), `${ms.month}`, "month");
      });
      if (leadNote) push("Milestone", "COD driver", leadNote, "");

      const csv = "\uFEFF" + rows.join("\n"); // BOM for Excel UTF-8
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Power_Configurator_${(project.name || "report").replace(/[^a-z0-9-_ ]/gi, "_")}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("report.csv_downloaded"));
      if (typeof window !== "undefined" && window.plausible) {
        window.plausible("report_csv_downloaded", { props: { project_id: project.id } });
      }
    } catch {
      toast.error(tr("Could not download CSV", "Impossible de télécharger le CSV"));
    }
  };

  return (
    <div className="App" style={{ background: "var(--paper)" }}>
      <Header />
      <div className="max-w-[1080px] mx-auto px-6 lg:px-10 py-10">
        <div className="no-print flex flex-wrap justify-between items-center gap-3 mb-6">
          <button onClick={() => navigate("/projects")} className="btn-ghost">
            <ArrowLeft size={14} className="inline" /> {tr("Back to projects", "Retour aux projets")}
          </button>
          <div className="flex gap-3">
            <button onClick={() => setTalkOpen(true)} className="btn-secondary" data-testid="report-talk">
              <PhoneCall size={13} /> {t("landing.hero.talk")}
            </button>
            <button onClick={() => setShareOpen(true)} className="btn-secondary" data-testid="report-share">
              <Share2 size={13} /> {tr("Share read-only", "Partager en lecture seule")}
            </button>
            <button onClick={downloadCsv} className="btn-secondary" data-testid="report-download-csv">
              <FileSpreadsheet size={13} /> {t("report.export_csv")}
            </button>
            <div
              role="group"
              aria-label="PDF language"
              data-testid="report-pdf-lang-toggle"
              className="inline-flex rounded-full overflow-hidden border"
              style={{ borderColor: "var(--rule)", background: "var(--paper)" }}
            >
              {["en", "fr"].map((code) => {
                const active = pdfLang === code;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setPdfLang(code)}
                    data-testid={`report-pdf-lang-${code}`}
                    aria-pressed={active}
                    className="px-3 py-1.5 text-xs font-mono uppercase tracking-widest transition-colors"
                    style={{
                      background: active ? "var(--brand-green)" : "transparent",
                      color: active ? "#fff" : "var(--muted)",
                      fontWeight: active ? 600 : 500,
                    }}
                  >
                    {code === "en" ? "EN" : "FR"}
                  </button>
                );
              })}
            </div>
            <button
              onClick={async () => {
                const safeName = (project.name || "report").replace(/[^a-z0-9-_ ]/gi, "_");
                await downloadPdf(`${API}/projects/${project.id}/pdf?lang=${pdfLang}`,
                  `Power_Configurator_${safeName}_${pdfLang.toUpperCase()}.pdf`);
              }}
              className="btn-primary"
              data-testid="report-download"
            >
              <Download size={13} /> {tr("Download PDF", "Télécharger le PDF")}
            </button>
            <div className="inline-flex items-stretch rounded overflow-hidden" style={{ border: "1px solid var(--hairline)" }}>
              <a
                href={`${API}/projects/${project.id}/xlsx`}
                download
                className="!py-1.5 !px-3 text-sm flex items-center gap-1.5"
                style={{ background: "var(--white)", color: "var(--char)" }}
                data-testid="report-download-xlsx"
                title={tr("Board-deck ready workbook — 9 tabs with live CAPEX/OPEX formulas", "Classeur prêt pour vos présentations — 9 onglets avec formules CAPEX/OPEX actives")}
              >
                <FileSpreadsheet size={13} /> Excel
              </a>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="!py-1.5 !px-2 flex items-center"
                    style={{ background: "var(--white)", color: "var(--char)", borderLeft: "1px solid var(--hairline)" }}
                    data-testid="report-download-xlsx-preset"
                    aria-label={tr("Choose Excel preset", "Choisir un préréglage Excel")}
                  >
                    <ChevronDown size={13} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="text-xs">
                  <DropdownMenuLabel>{tr("Download preset", "Préréglage de téléchargement")}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <a href={`${API}/projects/${project.id}/xlsx`} download data-testid="report-xlsx-preset-all">
                      <span className="flex flex-col">
                        <span className="font-medium">{tr("Full workbook", "Classeur complet")}</span>
                        <span style={{ color: "var(--muted)" }}>{tr("All 9 tabs", "Les 9\u00a0onglets")}</span>
                      </span>
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a href={`${API}/projects/${project.id}/xlsx?columns=finance`} download data-testid="report-xlsx-preset-finance">
                      <span className="flex flex-col">
                        <span className="font-medium">Finance</span>
                        <span style={{ color: "var(--muted)" }}>{tr("Summary + CAPEX + OPEX — for board decks", "Synthèse + CAPEX + OPEX — pour vos présentations")}</span>
                      </span>
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a href={`${API}/projects/${project.id}/xlsx?columns=technical`} download data-testid="report-xlsx-preset-technical">
                      <span className="flex flex-col">
                        <span className="font-medium">{tr("Technical", "Technique")}</span>
                        <span style={{ color: "var(--muted)" }}>{tr("Summary + Inputs + Power model + Equipment", "Synthèse + Données d'entrée + Modèle énergétique + Équipements")}</span>
                      </span>
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a href={`${API}/projects/${project.id}/xlsx?columns=permitting`} download data-testid="report-xlsx-preset-permitting">
                      <span className="flex flex-col">
                        <span className="font-medium">{tr("Permitting", "Réglementation")}</span>
                        <span style={{ color: "var(--muted)" }}>{tr("Summary + Roadmap + Permitting", "Synthèse + Feuille de route + Réglementation")}</span>
                      </span>
                    </a>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        <div className="card p-8 md:p-12" style={{ background: "var(--white)" }} data-testid="report-doc">
          <div className="pb-6 mb-8" style={{ borderBottom: "2px solid var(--charcoal)" }}>
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={14} style={{ color: "var(--brand-green)" }} />
              <div className="overline">{tr("Feasibility memorandum · Screening-level", "Mémorandum de faisabilité · Niveau préliminaire")}</div>
            </div>
            <h1 className="font-display text-4xl">{String(project.name || "").replace(/(^|\s)#(?=\S)/g, "$1")}</h1>
            <MemoMeta input={input} m={m} prio={prio} country={country} t={t} />
          </div>

          <CondFuelBanner result={r} />
          <Section n="01" title={tr("Executive summary", "Synthèse")}>
            {isFr ? (
              <p>
                Ce mémorandum présente un cadrage indicatif, de niveau préliminaire, pour une application du secteur
                {" "}<b>{sectorName(input.sector)}</b> — pays{"\u00a0"}: <b>{country}</b>.
                Le profil du site indique une pointe de charge de <b>{kw(input.peak_kw)}</b> (capacité ferme cible avec 20{"\u00a0"}% de réserve{"\u00a0"}: <b>{kw(m.firm_capacity_target_kw)}</b>)
                et une demande annuelle de <b>{kw(m.annual_demand_kwh || m.annual_energy_kwh, "kWh")}</b>, en fonctionnement
                {" "}<b>{memoGridFr(input.grid_status)}</b>.
              </p>
            ) : (
            <p>
              This memorandum presents an indicative, screening-level scope for {memoSector(input.sector).a}
              {" "}<b>{memoSector(input.sector).sector}</b> application in <b>{memoCountry(input.country)}</b>.
              The site profile indicates a peak load of <b>{fmt(input.peak_kw)} kW</b> (firm capacity target with 20% reserve: <b>{fmt(m.firm_capacity_target_kw)} kW</b>)
              and annual demand of <b>{fmt(m.annual_demand_kwh || m.annual_energy_kwh)} kWh</b>, operating
              {" "}<b>{memoGrid(input.grid_status)}</b>.
            </p>
            )}
            {isFr ? (
              <p className="mt-2" data-testid="exec-summary-model-sentence">
                {isFullThermal ? (
                  <>La centrale recommandée est une centrale tout thermique à base de{" "}
                    <b>{thermalTechName}</b>, sans capacité solaire, éolienne ni batterie, choisie pour la priorité{"\u00a0"}: <b>{prioInline}</b>.</>
                ) : isDieselOnly ? (
                  <>La centrale recommandée est une centrale diesel uniquement (groupes diesel), sans capacité
                    solaire, éolienne ni batterie, choisie pour la priorité{"\u00a0"}: <b>{prioInline}</b>.</>
                ) : (
                  <>La centrale recommandée utilise {memoPartsFr(m)}, choisie pour la priorité{"\u00a0"}: <b>{prioInline}</b>.</>
                )}{" "}
                CAPEX indicatif{"\u00a0"}: <b>{usd(cap.total)}</b> (fourchette {usd(cap.range_low)}–{usd(cap.range_high)}),
                LCOE indicatif <b>{lcoeTxt(fin.lcoe_usd_per_kwh)}</b>, retour sur investissement simple par rapport au cas de
                comparaison{"\u00a0"}: <b>{comparisonYears(fin.simple_payback_years, "fr")}</b>.
              </p>
            ) : (
            <p className="mt-2" data-testid="exec-summary-model-sentence">
              {isFullThermal ? (
                <>
                  The recommended plant is a full-thermal plant based on{" "}
                  <b>{thermalTechName}</b>, with no solar, wind or battery capacity, chosen for
                  the priority: <b>{PRIORITY_EN[input.priority] || input.priority?.replace(/_/g, " ")}</b>.
                </>
              ) : isDieselOnly ? (
                <>
                  The recommended plant is a diesel-only plant (diesel gensets), with no
                  solar, wind or battery capacity, chosen for the priority:{" "}
                  <b>{PRIORITY_EN[input.priority] || input.priority?.replace(/_/g, " ")}</b>.
                </>
              ) : (
                <>
                  The recommended plant uses {memoParts(m)}, chosen for the priority:{" "}
                  <b>{PRIORITY_EN[input.priority] || input.priority?.replace(/_/g, " ")}</b>.
                </>
              )}{" "}
              Indicative CAPEX is <b>{usd(cap.total)}</b> (range {usd(cap.range_low)}–{usd(cap.range_high)}),
              indicative LCOE <b>${fin.lcoe_usd_per_kwh}/kWh</b>, simple payback against the comparison case: <b>{comparisonYears(fin.simple_payback_years)}</b>.
            </p>
            )}
            <p className="mt-3 text-sm" style={{ color: "var(--muted)" }} data-testid="exec-summary-payback-note">
              {isFr ? (
                <>
                  <em>Note sur le retour sur investissement{"\u00a0"}:</em>{" "}
                  {gridCase ? (<>le retour simple est mesuré par rapport au cas de comparaison. Des tarifs réseau plus élevés
                  {" "}<b>raccourcissent</b> le retour, car l'approvisionnement évité coûte plus cher{"\u00a0"};{" "}</>) : (<>le retour simple est mesuré par rapport à une référence
                  tout diesel («{"\u00a0"}ne rien faire{"\u00a0"}»). Un prix du diesel plus élevé <b>raccourcit</b> le retour, car la référence
                  évitée devient plus coûteuse{"\u00a0"};{" "}</>)}
                  {isFullThermal || isDieselOnly
                    ? "cette centrale brûle son propre combustible, donc son OPEX suit le prix livré du combustible — c'est le contrat d'approvisionnement, et non le prix du combustible, qui protège le rendement."
                    : "l'OPEX de cette centrale ne suit le prix du combustible qu'en proportion du combustible qu'elle consomme."}
                </>
              ) : (<>
              <em>Note on payback:</em>{" "}
              {gridCase ? (<>the simple payback figure is measured against the comparison case. Higher grid tariffs
              {" "}<b>shorten</b> the payback because the supply you're avoiding costs more;{" "}</>) : (<>the simple payback figure is measured against a pure-diesel "do nothing"
              baseline. Higher diesel fuel prices <b>shorten</b> the payback because the baseline you're avoiding
              gets more painful;{" "}</>)}
              {isFullThermal || isDieselOnly
                ? "this plant burns its own fuel, so its OPEX moves with the delivered fuel price — the fuel contract, not the fuel price, is what protects the return."
                : "this plant's OPEX moves with the fuel price only in proportion to the fuel it burns."}
              </>)}
            </p>
          </Section>

          <Section n="02" title={tr("Optimised power model", "Modèle énergétique optimisé")}>
            <Table rows={[
              ["Solar PV", m.solar_kw ? kw(m.solar_kw, isFr ? "kWc" : "kWp") : "—"],
              ["Battery storage", m.battery_kwh ? `${kw(m.battery_kwh, "kWh")} · ${kw(m.battery_power_kw)}` : "—"],
              ["Diesel gensets", m.genset_kw ? (isFr ? `${m.genset_units} × ${kw(m.genset_unit_size_kw)} = ${kw(m.genset_kw)}` : `${m.genset_units} × ${m.genset_unit_size_kw} kW = ${m.genset_kw} kW`) : "—"],
              ["Wind", m.wind_kw ? kw(m.wind_kw) : "—"],
              ["HFO", m.hfo_kw ? kw(m.hfo_kw) : "—"],
              // iter45ar — an 80 MW OCGT plant used to be absent from this
              // table entirely: only `gas_kw` (reciprocating engines) had a
              // row, so the turbine the engine actually picked was invisible.
              // All three machines get a row, dashed when not selected.
              ["OCGT (open-cycle gas turbine)", thermalSizing(m.ocgt_kw)],
              ["CCGT (combined-cycle gas turbine)", thermalSizing(m.ccgt_kw)],
              ["Reciprocating gas engine", thermalSizing(m.gas_kw)],
              ["Annual demand", kw(m.annual_demand_kwh || m.annual_energy_kwh, "kWh")],
              ["Annual served (dispatch)", kw(m.annual_served_kwh || m.annual_energy_kwh, "kWh")],
              ["Annual generation (gross)", `${kw(m.annual_generation_kwh || m.annual_energy_kwh, "kWh")}${m.curtailment_pct ? (isFr ? ` · ${pct(m.curtailment_pct, 1, 0)} écrêtés` : ` · ${m.curtailment_pct}% curtailed`) : ""}`],
              ["Renewable fraction", pct(Math.round((m.renewable_fraction || 0) * 100), 0)],
              ["Firm capacity (target / available)", `${fmt(m.firm_capacity_target_kw)} / ${kw(m.firm_capacity_available_kw)} ${m.firm_capacity_ok ? "✓" : tr("insufficient", "insuffisante")}`],
              /* F3 — the pair, so "available" is never read as nameplate. */
              ...(firmBasisPair(m, lang) ? [["Firm capacity basis", firmBasisPair(m, lang)]] : []),
              ["Generator derate", pct(generatorDeratePct(m), 1, 0)],
              ["PV specific yield", isFr ? `${num(m.pv_yield_kwh_per_kwp_day, 2)}\u00a0kWh/kWc/jour` : `${m.pv_yield_kwh_per_kwp_day} kWh/kWp/day`],
            ]} />
          </Section>

          <Section n="03" title={tr("Equipment configuration", "Configuration des équipements")}>
            <Table rows={(r.equipment || []).map(equipmentRow).map((e) => [e.item + (e.size ? ` — ${e.size}` : ""), e.category])} />
          </Section>

          <Section n="04" title={tr("Budgetary costs", "Coûts budgétaires")}>
            <h4 className="font-display text-base mt-2 mb-3">{tr("CAPEX breakdown", "Détail du CAPEX")}</h4>
            <Table rows={(cap.breakdown || []).map((b) => [capexRowLabel(b), usd(b.capex_usd)])} />
            <div className="flex justify-between mt-3 py-2" style={{ borderTop: "1px solid var(--hairline)" }}>
              <span className="font-medium">{tr("Total CAPEX (indicative)", "CAPEX total (indicatif)")}</span>
              <span className="font-mono font-medium">{usd(cap.total)} <span className="text-xs" style={{ color: "var(--muted)" }}>({usd(cap.range_low)}–{usd(cap.range_high)})</span></span>
            </div>

            <h4 className="font-display text-base mt-6 mb-3">{tr("Annual OPEX", "OPEX annuel")}</h4>
            <Table rows={[
              [m.gas_kw > 0 ? tr("Fuel", "Combustible") : tr("Genset fuel", "Carburant des groupes"), usd(opex.fuel_cost)],
              ["Solar O&M", usd(opex.solar_om)],
              ...(m.battery_kwh > 0 ? [["Battery O&M", usd(opex.battery_om)]] : []),
              [m.gas_kw > 0 ? tr("Gas-engine O&M", "O&M moteurs à gaz") : tr("Genset O&M", "O&M groupes"), usd(opex.genset_om)],
              ["Grid import", usd(opex.grid_cost)],
              ["Total annual OPEX", usd(opex.annual)],
            ]} />

            <h4 className="font-display text-base mt-6 mb-3">{tr("Financial indicators", "Indicateurs financiers")}</h4>
            <Table rows={[
              ["Indicative LCOE", isFr ? `${lcoeTxt(fin.lcoe_usd_per_kwh)} (fourchette ${lcoeTxt(fin.lcoe_low)} – ${lcoeTxt(fin.lcoe_high)})` : `$${fin.lcoe_usd_per_kwh}/kWh (range $${fin.lcoe_low} – $${fin.lcoe_high})`],
              ["Discount rate", pct(Math.round(fin.discount_rate * 100), 0)],
              ["Diesel-only baseline CAPEX", usd(fin.baseline_diesel_only_capex)],
              gridCase
                ? [isFr ? `OPEX du cas de comparaison (annuel) — ${cfName}` : `Comparison case OPEX (annual) — ${cfName}`,
                   usd(fin.counterfactual?.annual_opex_usd ?? fin.baseline_diesel_only_opex_annual)]
                : ["Diesel-only baseline OPEX (annual)", usd(fin.baseline_diesel_only_opex_annual)],
              [gridCase ? (isFr ? `Économies annuelles vs ${cfName} (A1)` : `Annual savings vs ${cfName} (Y1)`) : "Annual savings vs diesel-only (Y1)",
                comparisonMoney(fin.annual_savings_vs_diesel_yr1, usd, lang)],
              ["Simple payback", comparisonYears(fin.simple_payback_years, lang)],
            ]} />
          </Section>

          <Section n="05" title={`${tr("Permitting & regulation", "Autorisations et réglementation")} — ${shownPermits.country ? (isFr ? t(`country.${shownPermits.country}`, shownPermits.country) : shownPermits.country) : country}`}>
            <p className="mb-3 text-sm" style={{ color: "var(--muted)" }}>{tr("Regime:", "Régime\u00a0:")} {regimeLabel(permits.regime, lang)}</p>
            <div className="overflow-x-auto max-w-full" data-testid="permit-table-wrap">
            <table className="w-full text-sm" style={{ borderCollapse: "collapse", tableLayout: "fixed", overflowWrap: "anywhere" }} data-testid="permit-table">
              <tbody>
                {(shownPermits.items || []).map((it, i) => (
                  <tr key={i} style={{ borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
                    <td className="py-2.5 pr-4 align-top" style={{ color: "var(--body)" }}>
                      {it.item}{it.notes ? <span style={{ color: "var(--muted)" }}> — {it.notes}</span> : null}
                    </td>
                    <td className="py-2.5 align-top text-right">
                      <span style={{ color: "var(--muted)" }}>{it.status}</span>
                      {it.authority && (
                        <>
                          <span style={{ color: "var(--muted)" }}> · </span>
                          <RegulatorAuthority authority={it.authority} meta={it.regulator_meta} />
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Section>

          <Section n="06" title={tr("Initial project & financial plan", "Plan initial de projet et de financement")}>
            <h4 className="font-display text-base mb-3">{t("report.roadmap.title")}</h4>
            <Table rows={roadmapRows(milestones, sched, t)} />
            {leadNote && (
              <p className="text-xs mt-3" style={{ color: "var(--muted)" }} data-testid="report-roadmap-lead-note">
                {leadNote}
              </p>
            )}

            <h4 className="font-display text-base mt-6 mb-3">{tr("CAPEX financing outline (illustrative)", "Schéma de financement du CAPEX (illustratif)")}</h4>
            <Table rows={[
              ["Equity (~30%)", usd(cap.total * 0.30)],
              ["Senior debt (~55%)", usd(cap.total * 0.55)],
              ["Grants / mezzanine (~15%)", usd(cap.total * 0.15)],
            ]} />

            <p className="text-sm mt-6" style={{ color: "var(--muted)" }}>
              {tr("Indicative structure only. Actual gearing depends on off-taker credit, tenor and jurisdiction.",
                "Structure indicative uniquement. Le niveau d'endettement réel dépend de la qualité de crédit de l'acheteur, de la maturité et de la juridiction.")}
            </p>
          </Section>

          <div className="mt-10 pt-6" style={{ borderTop: "1px solid var(--hairline)" }}>
            <p className="text-xs" style={{ color: "var(--muted)" }}>
              {tr(`The Power Configurator provides indicative, screening-level outputs for information only and does not
              constitute engineering, financial or investment advice. Numbers derived from published benchmarks and
              editable coefficients; site-specific verification required.`,
                "Le configurateur fournit des résultats indicatifs, de niveau préliminaire, à titre informatif uniquement, et ne constitue pas un conseil d'ingénierie, financier ou d'investissement. Les chiffres reposent sur des références publiées et des coefficients modifiables\u00a0; une vérification propre au site est nécessaire.")}
            </p>
          </div>
        </div>
      </div>
      <Footer />
      <TalkToEngineerModal open={talkOpen} onClose={() => setTalkOpen(false)} defaults={{ country: input.country, summary: `${[input.sector, applicationLabel(input)].filter(Boolean).join(" — ")} in ${input.country}, ${fmt(input.peak_kw)} kW peak.` }} projectId={project.id} source="report" />
      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} projectId={project.id} projectName={project.name} />
    </div>
  );
};

const Section = ({ n, title, children }) => (
  <div className="mt-8" data-testid={`report-section-${n}`}>
    <div className="flex items-baseline gap-3 mb-3">
      <span className="font-mono text-xs" style={{ color: "var(--brand-green)" }}>{n}</span>
      <h2 className="font-display text-xl">{title}</h2>
    </div>
    <div className="text-sm" style={{ color: "var(--body)" }}>{children}</div>
  </div>
);

// Table row labels stay English in source; French is looked up at render.
const ROW_FR = {
  "Solar PV": "Solaire PV", "Battery storage": "Stockage batterie", "Diesel gensets": "Groupes diesel", Wind: "Éolien",
  "OCGT (open-cycle gas turbine)": "OCGT (turbine à gaz à cycle ouvert)", "CCGT (combined-cycle gas turbine)": "CCGT (turbine à gaz à cycle combiné)",
  "Reciprocating gas engine": "Moteur alternatif à gaz", "Annual demand": "Demande annuelle",
  "Annual served (dispatch)": "Énergie servie annuelle (dispatch)", "Annual served": "Énergie servie annuelle",
  "Annual generation (gross)": "Production annuelle (brute)", "Renewable fraction": "Part renouvelable",
  "Firm capacity (target / available)": "Capacité ferme (cible / disponible)", "Firm capacity basis": "Base de la capacité ferme",
  "Generator derate": "Déclassement des groupes", "PV specific yield": "Productible spécifique PV",
  "Solar O&M": "O&M solaire", "Battery O&M": "O&M batterie", "Grid import": "Achats au réseau", "Total annual OPEX": "OPEX annuel total",
  "Indicative LCOE": "LCOE indicatif", "Discount rate": "Taux d'actualisation",
  "Diesel-only baseline CAPEX": "CAPEX de la référence diesel seul", "Diesel-only baseline OPEX (annual)": "OPEX de la référence diesel seul (annuel)",
  "Annual savings vs diesel-only (Y1)": "Économies annuelles vs diesel seul (A1)", "Simple payback": "Retour sur investissement simple",
  "Equity (~30%)": "Fonds propres (~30\u00a0%)", "Senior debt (~55%)": "Dette senior (~55\u00a0%)", "Grants / mezzanine (~15%)": "Subventions / mezzanine (~15\u00a0%)",
  "Total CAPEX": "CAPEX total", "Annual OPEX": "OPEX annuel", Payback: "Retour sur investissement",
};
const rowLabel = (k) => (getLocale() === "fr" ? ROW_FR[k] ?? k : k);

const Table = ({ rows }) => (
  <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
    <tbody>
      {rows.map(([k, v]) => (
        <tr key={String(k)} style={{ borderBottom: "1px solid var(--hairline)" }}>
          <td className="py-2 pr-4" style={{ color: "var(--body)" }}>{rowLabel(k)}</td>
          <td className="py-2 text-right font-mono" style={{ color: "var(--charcoal)" }}>{v}</td>
        </tr>
      ))}
    </tbody>
  </table>
);
